import React, { useState, useEffect } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import API from './api';
import ProductNav from './components/ProductNav';
import SideHelpPanel from './components/SideHelpPanel';

const PROF_CREATION_TYPES = [
  { key: 'hero', engine: 'professional-hero-primary-visual',        name: 'Hero & Primary Visual',   desc: 'Primary subject and hero visual generation — portraits, professional talent, product heroes, and campaign lead visuals.',             points: ['Professional talent & character heroes', 'Campaign lead and hero shots', 'Cultural and identity-forward visuals', 'Role and brand representation'],                        route: '/generators?library=professional&category=hero',        icon: '👤', color: '#7c3aed', gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)', colSpan: 3 },
  { key: 'product', engine: 'professional-product-object',     name: 'Product & Object',        desc: 'Product photography and object visualization — clean renders, lifestyle setups, e-commerce visuals, packaging, and industrial design.', points: ['E-commerce and retail product renders', 'Packaging and brand object visuals', 'Industrial and automotive object design', 'UI-ready product assets'],                       route: '/generators?library=professional&category=product',     icon: '📦', color: '#0ea5e9', gradient: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', colSpan: 3 },
  { key: 'promotional', engine: 'professional-promotional-ad', name: 'Promotional & Ad',        desc: 'Ad campaign and promotional visual generation — brand campaigns, marketing creatives, social ad formats, and commercial imagery.',      points: ['Brand campaign visuals', 'Social media ad formats', 'Commercial marketing creatives', 'Persuasive visual systems'],                                                       route: '/generators?library=professional&category=promotional', icon: '📣', color: '#dc2626', gradient: 'linear-gradient(135deg, #dc2626, #f87171)', colSpan: 2 },
  { key: 'editorial', engine: 'professional-editorial-lifestyle',   name: 'Editorial & Lifestyle',   desc: 'Editorial photography and lifestyle visual generation — magazine content, food, fashion, fitness, and travel.',                         points: ['Magazine and editorial formats', 'Lifestyle and brand story content', 'Food, fashion, fitness, and travel', 'Seasonal and campaign editorial'],                          route: '/generators?library=professional&category=editorial',   icon: '📰', color: '#059669', gradient: 'linear-gradient(135deg, #059669, #34d399)', colSpan: 2 },
  { key: 'interface', engine: 'professional-interface-structured',   name: 'Interface & Structured',  desc: 'UI/UX, structured infographic, data visualization, and interface-ready visual generation.',                                            points: ['UI and UX component visuals', 'Infographic and data visualization', 'Dashboard and structured layouts', 'Interface-ready asset systems'],                              route: '/generators?library=professional&category=interface',   icon: '🖥️', color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', colSpan: 2 },
  { key: 'thumbnail', engine: 'professional-thumbnail-attention',   name: 'Thumbnail & Attention',   desc: 'Scroll-stopping, high-attention thumbnail and cover visual generation — YouTube, social, course covers, and campaign hooks.',          points: ['YouTube and social thumbnails', 'Course and content cover images', 'High-contrast attention-driving formats', 'Click-optimized visual hooks'],                          route: '/generators?library=professional&category=thumbnail',   icon: '⚡', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', colSpan: 2 },
];

function ProfCreationCard({ type, onNavigate, status }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ ...bm.box, ...(hovered ? bm.boxHover : {}), borderColor: hovered ? type.color : 'rgba(255,255,255,0.12)', boxShadow: hovered ? `0 12px 40px rgba(0,0,0,0.35), 0 0 0 1px ${type.color}44` : '0 8px 32px rgba(0,0,0,0.2)', gridColumn: `span ${type.colSpan}` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(type.route)}
    >
      <div style={bm.boxTop}>
        <div style={{ ...bm.boxIcon, background: type.gradient }}>{type.icon}</div>
        <div>
          <div style={bm.boxName}>{type.name}</div>
          {status === 'live'
            ? <div style={{ ...bm.liveTag, background: `${type.color}22`, color: type.color, borderColor: `${type.color}44` }}>Live</div>
            : status === 'unavailable'
              ? <div style={{ ...bm.liveTag, background: 'rgba(120,120,120,0.14)', color: '#6b7280', borderColor: 'rgba(120,120,120,0.35)' }}>Preparing</div>
              : <div style={{ ...bm.liveTag, background: 'rgba(120,120,120,0.10)', color: '#9ca3af', borderColor: 'rgba(120,120,120,0.25)' }}>Checking…</div>}
        </div>
      </div>
      <p style={bm.boxDesc}>{type.desc}</p>
      <ul style={bm.pointsList}>
        {type.points.map((pt, i) => (
          <li key={i} style={bm.pointItem}><span style={{ ...bm.pointDot, background: type.color }} />{pt}</li>
        ))}
      </ul>
      <div style={{ display: 'flex', gap: 10, marginTop: 'auto' }}>
        <button
          style={{ ...bm.openBtn, ...glassSurface(type.color, type.color, type.color), flex: 1 }}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
          onClick={e => { e.stopPropagation(); onNavigate(type.route); }}
        >Browse Generators</button>
      </div>
    </div>
  );
}

const bm = {
  grid:     { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 20 },
  box:      { background: 'rgba(255,255,255,0.97)', border: '2px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 14, cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s', height: '100%', boxSizing: 'border-box' },
  boxHover: { transform: 'translateY(-3px)' },
  boxTop:   { display: 'flex', alignItems: 'center', gap: 12 },
  boxIcon:  { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 },
  boxName:  { fontWeight: 800, fontSize: 16, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 4 },
  liveTag:  { display: 'inline-block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 20, border: '1px solid' },
  boxDesc:  { fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: 0 },
  pointsList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 7, flex: 1 },
  pointItem:  { display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, color: '#374151' },
  pointDot:   { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  openBtn:    { color: '#fff', border: 'none', borderRadius: 10, padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%', boxShadow: '0 4px 14px rgba(0,0,0,0.18)', transition: 'filter 0.15s, transform 0.15s', letterSpacing: '0.01em' },
};

export default function ProfessionalEngines() {
  const navigate = useNavigate();

  // Availability is reported by the backend engine registry rather than asserted
  // in the UI. Before the 2026-08-25 repair all six cards were hard-coded "Live"
  // with no Professional backend behind them at all.
  const [liveEngines, setLiveEngines] = useState(null);
  useEffect(() => {
    let cancelled = false;
    API.get('/v1/professional/engines')
      .then(res => {
        if (cancelled) return;
        const cats = (res.data?.engines || []).map(e => e.portal_category);
        setLiveEngines(cats);
      })
      .catch(() => { if (!cancelled) setLiveEngines([]); });
    return () => { cancelled = true; };
  }, []);

  const statusFor = (type) => {
    if (liveEngines === null) return 'checking';
    return liveEngines.includes(type.key) ? 'live' : 'unavailable';
  };

  return (
    <div style={s.page}>
      <ProductNav />
      <SideHelpPanel title="Professional Creation Suite">
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
          <div>
            <span style={hp.heading}>What This Page Is</span>
            <p style={hp.body}>The Professional Creation Suite gives you six connected production workflows for building commercial visuals, brand content, and deployable media assets.</p>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>The Six Types</span>
            <ul style={hp.list}>
              <li style={hp.item}><span style={hp.bold}>Hero & Primary Visual</span> — Portraits, professional talent, brand heroes, campaign leads</li>
              <li style={hp.item}><span style={hp.bold}>Product & Object</span> — E-commerce renders, packaging, industrial, automotive</li>
              <li style={hp.item}><span style={hp.bold}>Promotional & Ad</span> — Brand campaigns, social ad formats, marketing creatives</li>
              <li style={hp.item}><span style={hp.bold}>Editorial & Lifestyle</span> — Magazine, food, fashion, fitness, travel content</li>
              <li style={hp.item}><span style={hp.bold}>Interface & Structured</span> — UI/UX, infographics, dashboards, data visuals</li>
              <li style={hp.item}><span style={hp.bold}>Thumbnail & Attention</span> — YouTube covers, social thumbnails, course imagery</li>
            </ul>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>How To Use</span>
            <ol style={hp.list}>
              <li style={hp.item}>Choose the creation type that matches your production task</li>
              <li style={hp.item}>Browse generators within each category</li>
              <li style={hp.item}>Configure selections across the generation layers</li>
              <li style={hp.item}>Generate your output</li>
            </ol>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>Tips</span>
            <ul style={hp.list}>
              <li style={hp.item}>Start with Hero and Product to establish core visual identity</li>
              <li style={hp.item}>Build Promotional and Editorial assets around your established visuals</li>
              <li style={hp.item}>All creation types draw from the same professional production ecosystem</li>
            </ul>
          </div>
        </div>
      </SideHelpPanel>

      <div className="vi-desktop-wide" style={s.container}>

        <div style={s.productBar}>
          <div style={s.productIndicator}>💼 Professional Generators</div>
          <div style={s.productTabs}>
            <button style={s.productTab} onClick={() => navigate('/dashboard/professional')}>Dashboard</button>
            <button style={{ ...s.productTab, ...s.productTabActive }}>Create</button>
            <button style={s.productTab} onClick={() => navigate('/generators?library=professional')}>Generator Library</button>
          </div>
        </div>

        <div style={s.welcomeBanner}>
          <div>
            <h1 style={s.welcomeTitle}>Professional Creation Suite</h1>
            <p style={s.welcomeSub}>Six production-grade creation workflows. Build hero visuals, product renders, ad campaigns, editorial content, UI assets, and attention-driving thumbnails.</p>
          </div>
          <button style={s.upgradeCta} onClick={() => navigate('/upgrade')}>View Plans &rarr;</button>
        </div>

        <div style={s.valueStrip}>
          <span style={s.valueItem}>👤 Hero Visuals</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>📦 Product & Object</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>📣 Promotional & Ad</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>📰 Editorial & Lifestyle</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>🖥️ Interface & Structured</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>⚡ Thumbnail & Attention</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>Commercial production ecosystem</span>
        </div>

        <div style={{ ...bm.grid, marginBottom: 32 }}>
          {PROF_CREATION_TYPES.map(type => (
            <ProfCreationCard key={type.key} type={type} onNavigate={navigate} status={statusFor(type)} />
          ))}
        </div>

        <div style={s.ecosystemSection}>
          <div style={s.ecosystemHeader}>
            <div style={s.ecosystemTitle}>How the Creation Workflows Connect</div>
            <div style={s.ecosystemSub}>Each creation type handles a distinct layer of your production pipeline. Use them individually or chain them into a complete commercial workflow.</div>
          </div>
          <div style={s.workflowGrid}>
            {[
              { step: '01', icon: '👤', title: 'Hero',        desc: 'Define the primary visual subject or character for your campaign.' },
              { step: '02', icon: '📦', title: 'Product',     desc: 'Build the object, product, or artifact at the center of your work.' },
              { step: '03', icon: '📣', title: 'Promotional', desc: 'Wrap hero and product in campaign-ready ad formats.' },
              { step: '04', icon: '📰', title: 'Editorial',   desc: 'Layer lifestyle and story context around your brand visual.' },
              { step: '05', icon: '🖥️', title: 'Interface',   desc: 'Structure data, UI components, and infographic systems.' },
              { step: '06', icon: '⚡', title: 'Thumbnail',   desc: 'Drive attention with optimized scroll-stopping cover visuals.' },
            ].map((item, i) => (
              <div key={i} style={s.workflowStep}>
                <div style={s.workflowNum}>{item.step}</div>
                <div style={s.workflowIcon}>{item.icon}</div>
                <div style={s.workflowTitle}>{item.title}</div>
                <p style={s.workflowDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={s.ecosystemSection}>
          <div style={s.ecosystemHeader}>
            <div style={s.ecosystemTitle}>What You Can Create</div>
            <div style={s.ecosystemSub}>Every output type available across the six professional creation workflows.</div>
          </div>
          <div style={s.createGrid}>
            {[
              { icon: '👤', title: 'Professional Portraits',  desc: 'Fashion designers, architects, medical professionals, athletes — role-forward hero visuals built from structured profession inputs.' },
              { icon: '📦', title: 'Product Renders',         desc: 'E-commerce photography, packaging visualization, automotive renders, and industrial design object outputs.' },
              { icon: '📣', title: 'Ad Campaigns',            desc: 'Brand campaign creatives, social media ad formats, marketing imagery, and persuasive commercial visual systems.' },
              { icon: '📰', title: 'Lifestyle Content',       desc: 'Magazine editorial, food photography, travel content, fitness imagery, and seasonal brand story visuals.' },
              { icon: '🖥️', title: 'UI & Data Visuals',      desc: 'Dashboard designs, infographic systems, UI component layouts, and structured interface-ready visual assets.' },
              { icon: '⚡', title: 'Attention Thumbnails',    desc: 'YouTube covers, course thumbnails, social hooks, and high-contrast click-optimized campaign visuals.' },
            ].map((item, i) => (
              <div key={i} style={s.createCard}>
                <div style={s.createIcon}>{item.icon}</div>
                <div style={s.createTitle}>{item.title}</div>
                <p style={s.createDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={s.planNudgeCard}>
          <div style={s.planNudgeLeft}>
            <div style={s.planNudgeIcon}>💼</div>
            <div>
              <div style={s.planNudgeTitle}>Full access is included in the Creator plan and above.</div>
              <div style={s.planNudgeSub}>Hundreds of generators across all six creation types. Fashion, architecture, medical, film, games, technology, and more.</div>
            </div>
          </div>
          <div style={s.planNudgeRight}>
            <button style={s.planNudgeBtn} onClick={() => navigate('/upgrade')}>View Plans &rarr;</button>
          </div>
        </div>

        <div style={s.scaleSignal}>6 creation types &bull; 8,000+ generators &bull; commercial production ecosystem</div>

      </div>
    </div>
  );
}

const s = {
  page:             { minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c4a6e 100%)', fontFamily: "'Segoe UI', sans-serif" },
  container:        { maxWidth: 1200, margin: '0 auto', padding: '28px 40px' },
  productBar:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  productIndicator: { display: 'flex', alignItems: 'center', gap: 8, borderRadius: 20, padding: '6px 14px', color: '#fff', fontWeight: 700, fontSize: 14, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)' },
  productTabs:      { display: 'flex', gap: 8 },
  productTab:       { background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: 10, padding: '9px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.15s ease' },
  productTabActive: { borderColor: '#0ea5e9', color: '#fff', background: 'rgba(14,165,233,0.15)', boxShadow: '0 0 12px rgba(14,165,233,0.3), inset 0 0 0 1px #0ea5e9' },
  welcomeBanner:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 },
  welcomeTitle:     { color: '#fff', fontSize: 28, fontWeight: 800, margin: '0 0 6px' },
  welcomeSub:       { color: 'rgba(255,255,255,0.6)', fontSize: 15, margin: 0 },
  upgradeCta:       { background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' },
  valueStrip:       { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 24, color: 'rgba(255,255,255,0.4)', fontSize: 13, fontStyle: 'italic' },
  valueItem:        { color: 'rgba(255,255,255,0.55)', fontWeight: 600, fontStyle: 'normal' },
  valueSep:         { color: 'rgba(255,255,255,0.2)' },
  ecosystemSection: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '28px 28px 20px', marginBottom: 24 },
  ecosystemHeader:  { marginBottom: 24 },
  ecosystemTitle:   { color: '#fff', fontWeight: 800, fontSize: 20, marginBottom: 6 },
  ecosystemSub:     { color: 'rgba(255,255,255,0.5)', fontSize: 13 },
  workflowGrid:     { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16 },
  workflowNum:      { fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#38bdf8', letterSpacing: '0.1em', textTransform: 'uppercase' },
  workflowIcon:     { fontSize: 28 },
  workflowTitle:    { fontWeight: 800, fontSize: 14, color: '#1a1a1a' },
  workflowDesc:     { fontSize: 12, color: '#6b7280', lineHeight: 1.55, margin: 0 },
  workflowStep:     { background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 },
  createGrid:       { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
  createCard:       { background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 8 },
  createIcon:       { fontSize: 24 },
  createTitle:      { fontWeight: 800, fontSize: 15, color: '#1a1a1a' },
  createDesc:       { fontSize: 13, color: '#6b7280', lineHeight: 1.55, margin: 0 },
  planNudgeCard:    { background: 'rgba(255,255,255,0.92)', borderRadius: 16, padding: '16px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 16, flexWrap: 'wrap', border: '1px solid rgba(14,165,233,0.3)' },
  planNudgeLeft:    { display: 'flex', alignItems: 'center', gap: 16, flex: 1 },
  planNudgeIcon:    { width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 },
  planNudgeTitle:   { fontWeight: 800, fontSize: 15, color: '#1a1a1a', marginBottom: 3 },
  planNudgeSub:     { fontSize: 13, color: '#6b7280' },
  planNudgeRight:   { display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' },
  planNudgeBtn:     { ...glassSurface('#0369A1', '#0ea5e9', '#6366f1'), borderRadius: 10, padding: '11px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  scaleSignal:      { color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', fontStyle: 'italic', letterSpacing: '0.02em', paddingBottom: 8 },
};

const hp = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  list:    { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px' },
  bold:    { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};
